import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Search, Download, Plus, RefreshCw, Eye, Edit, Trash2, MoreVertical, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import MainService from '../../services/MainService';
import { normalizeVehicleGridResponse } from '../../utils/vehicleApiUtils';
import * as XLSX from 'xlsx';

const VehicleList = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    loadVehicles();
  }, [currentPage, searchTerm, pageSize]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const data = {
        Page: currentPage,
        PageSize: pageSize === 'all' ? totalCount || 1000 : pageSize,
        Keyword: searchTerm
      };

      if (pageSize === 'all' && totalCount === 0) {
        const countResponse = await MainService.VehiclesInGrid({ Page: 1, PageSize: 1 });
        const { totalCount: tc } = normalizeVehicleGridResponse(countResponse);
        setTotalCount(tc);
        data.PageSize = tc || 1000;
      }

      const response = await MainService.VehiclesInGrid(data);
      const { data: vehiclesList, totalCount: tc } = normalizeVehicleGridResponse(response);
      setVehicles(Array.isArray(vehiclesList) ? vehiclesList : []);
      if (tc > 0) {
        setTotalCount(tc);
      } else if (!totalCount) {
        setTotalCount(Array.isArray(vehiclesList) ? vehiclesList.length : 0);
      }
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  const sortVehicles = (vehicles) => {
    if (!sortField) return vehicles;

    return [...vehicles].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };

  const sortedVehicles = sortVehicles(vehicles);

  const handleMenuOpen = (event, vehicle) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicle);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVehicle(null);
  };

  const handleView = (vehicle) => {
    handleMenuClose();
    navigate(`/home/vehicle-management/vehicle-details/${vehicle.Id}`);
  };

  const handleEdit = (vehicle) => {
    handleMenuClose();
    navigate(`/home/vehicle-management/add-edit-vehicle/${vehicle.Id}`);
  };

  const handleDelete = (vehicle) => {
    handleMenuClose();
    // TODO: Implement delete functionality
    setError('Delete functionality will be implemented after API is ready');
  };

  const handleAddNew = () => {
    navigate('/home/vehicle-management/add-edit-vehicle');
  };

  const exportToExcel = () => {
    try {
      const data = sortedVehicles.map((vehicle, index) => ({
        '#': pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1,
        'Vehicle Number': vehicle.Number || '',
        'Model': vehicle.Model || '',
        'Status': vehicle.IsActive ? 'Active' : 'Inactive',
        'Created Date': vehicle.CreatedDate || ''
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
      XLSX.writeFile(wb, `vehicles_${new Date().toISOString().split('T')[0]}.xlsx`);
      setSuccess('Vehicles exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export vehicles');
    }
  };

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Truck className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Vehicle List</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Vehicle</span>
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={loadVehicles}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search vehicles by number, model..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 backdrop-blur-sm z-20 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('Id')}>
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('Number')}>
                  Vehicle Number {sortField === 'Number' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('Model')}>
                  Model {sortField === 'Model' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('IsActive')}>
                  Status {sortField === 'IsActive' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedVehicles.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No vehicles found
                  </td>
                </tr>
              ) : (
                sortedVehicles.map((vehicle, index) => (
                  <tr key={vehicle.Id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{vehicle.Number || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{vehicle.Model || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        vehicle.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {vehicle.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <button
                        type="button"
                        onClick={(e) => handleMenuOpen(e, vehicle)}
                        title="Actions"
                        className="p-2 text-[#46955A] hover:bg-[#f0f9f4] rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {totalCount > 0 
              ? pageSize === 'all'
                ? `1-${totalCount} out of ${totalCount}`
                : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} out of ${totalCount}`
              : '0 out of 0'}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#46955A]"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
      </div>

      {/* Options Menu */}
      {anchorEl && (
        <div className="fixed inset-0 z-40" onClick={handleMenuClose}></div>
      )}
      {anchorEl && (
        <div
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-1.5"
          style={{
            top: anchorEl.getBoundingClientRect().bottom + window.scrollY + 4,
            left: anchorEl.getBoundingClientRect().left + window.scrollX
          }}
        >
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleView(selectedVehicle)}
              title="View"
              aria-label="View"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleEdit(selectedVehicle)}
              title="Edit"
              aria-label="Edit"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(selectedVehicle)}
              title="Delete"
              aria-label="Delete"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;

